import React, { useState } from 'react';
import {
  User,
  ShieldCheck,
  Volume2,
  VolumeX,
  Vibrate,
  Sparkles,
  Server,
  Star,
  Bot,
  Cpu,
  History,
  ArrowRight,
  CheckCircle2,
} from 'lucide-react';
import type { UserProfileResponse } from '@nova-mine/shared';
import { formatCoinAmount } from '@nova-mine/shared';
import { GlassCard } from '../components/common/GlassCard.js';
import { AnimatedButton } from '../components/common/AnimatedButton.js';
import { sound } from '../utils/sound.js';

interface ProfileScreenProps {
  profile: UserProfileResponse | null;
  user: { id: number; first_name: string; username?: string; last_name?: string } | null;
  isDemoMode: boolean;
  onToggleDemoMode: () => void;
  onNavigateToActivity?: () => void;
}

export const ProfileScreen: React.FC<ProfileScreenProps> = ({
  profile,
  user,
  isDemoMode,
  onToggleDemoMode,
  onNavigateToActivity,
}) => {
  const [soundEnabled, setSoundEnabled] = useState(sound.enabled);
  const [hapticEnabled, setHapticEnabled] = useState(true);

  const toggleSound = () => {
    const next = !soundEnabled;
    setSoundEnabled(next);
    sound.setSoundEnabled(next);
    if (next) sound.playCoinChime();
  };

  const toggleHaptic = () => {
    setHapticEnabled(!hapticEnabled);
    sound.playTap();
  };

  const displayName = profile?.first_name || user?.first_name || 'Nova Pioneer';
  const displayUsername = profile?.username || user?.username || 'novapioneer';
  const telegramId = profile?.telegram_id || user?.id || 88888888;
  const starsCount = profile?.mining.stars.total_units || 0;
  const isAutoUnlocked = profile?.mining.auto.unlocked ?? false;

  return (
    <div className="w-full space-y-5 animate-fade-in pb-4">
      {/* 1. Telegram Profile Card */}
      <GlassCard variant="purple" className="border-purple-500/35 p-6 relative overflow-hidden">
        <div className="flex items-center space-x-4">
          {/* Avatar with Radiant Gradient Rim */}
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-tr from-cyan-400 via-indigo-500 to-purple-600 flex items-center justify-center text-white text-2xl font-black shadow-xl shadow-purple-500/25 border-2 border-white/20 flex-shrink-0">
            {displayName.charAt(0).toUpperCase()}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center space-x-2">
              <h3 className="text-lg font-black text-white truncate">{displayName}</h3>
              <ShieldCheck className="w-4 h-4 text-nova-cyan flex-shrink-0" />
            </div>

            <p className="text-xs text-purple-200 font-mono mt-0.5 truncate">@{displayUsername}</p>

            <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">
                TG ID: {telegramId}
              </span>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-400/20 text-nova-gold border border-amber-400/35">
                {starsCount > 0 ? `VIP Stars Fleet (${starsCount}x)` : 'Core Pioneer'}
              </span>
            </div>
          </div>
        </div>
      </GlassCard>

      {/* 2. Mining Rig Fleet Summary */}
      <GlassCard variant="deep" className="border-white/10 p-5 space-y-3">
        <h4 className="text-xs font-black uppercase tracking-wider text-gray-300">
          Mining Rig Fleet Status
        </h4>

        <div className="grid grid-cols-3 gap-2.5 text-center">
          <div className="rounded-2xl bg-black/35 border border-white/5 p-3">
            <Cpu className="w-5 h-5 text-purple-400 mx-auto mb-1" />
            <span className="text-[10px] text-gray-400 block font-bold">Main Core</span>
            <span className="text-xs font-bold text-white block mt-0.5">Active</span>
          </div>

          <div className="rounded-2xl bg-black/35 border border-white/5 p-3">
            <Bot className="w-5 h-5 text-cyan-400 mx-auto mb-1" />
            <span className="text-[10px] text-gray-400 block font-bold">Autonomous</span>
            <span className="text-xs font-bold text-cyan-300 block mt-0.5">
              {isAutoUnlocked ? 'Unlocked' : 'Locked'}
            </span>
          </div>

          <div className="rounded-2xl bg-black/35 border border-white/5 p-3">
            <Star className="w-5 h-5 text-nova-gold fill-current mx-auto mb-1" />
            <span className="text-[10px] text-gray-400 block font-bold">Stars Rigs</span>
            <span className="text-xs font-bold text-nova-gold block mt-0.5">
              {starsCount} Unit{starsCount !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </GlassCard>

      {/* 3. Transaction History Entry Point */}
      {onNavigateToActivity && (
        <GlassCard
          variant="interactive"
          onClick={onNavigateToActivity}
          className="border-white/10 p-4"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-500/20 border border-indigo-400/30 flex items-center justify-center text-indigo-300">
                <History className="w-5 h-5 text-indigo-300" />
              </div>
              <div>
                <h4 className="text-xs font-bold text-white">Immutable Ledger & Activity</h4>
                <p className="text-[11px] text-gray-400">View real-time claim records, rewards & audit trail</p>
              </div>
            </div>

            <ArrowRight className="w-4 h-4 text-gray-400" />
          </div>
        </GlassCard>
      )}

      {/* 4. Application Preferences & Toggles */}
      <GlassCard variant="deep" className="border-white/10 p-5 space-y-4">
        <h4 className="text-xs font-black uppercase tracking-wider text-gray-300">
          Preferences & Controls
        </h4>

        <div className="space-y-3.5">
          {/* Sound Synthesizer */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-xl bg-white/5 flex items-center justify-center text-gray-300">
                {soundEnabled ? <Volume2 className="w-4 h-4 text-cyan-400" /> : <VolumeX className="w-4 h-4 text-gray-500" />}
              </div>
              <div>
                <div className="text-xs font-bold text-white">Audio Chimes</div>
                <div className="text-[10px] text-gray-400">Synthesized coin and claim feedback</div>
              </div>
            </div>

            <button
              onClick={toggleSound}
              className={`w-11 h-6 rounded-full transition-colors relative ${
                soundEnabled ? 'bg-cyan-500' : 'bg-gray-800'
              }`}
            >
              <div
                className={`w-4 h-4 rounded-full bg-white transition-transform absolute top-1 ${
                  soundEnabled ? 'left-6' : 'left-1'
                }`}
              />
            </button>
          </div>

          {/* Haptic Vibration */}
          <div className="flex items-center justify-between pt-3 border-t border-white/5">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-xl bg-white/5 flex items-center justify-center text-gray-300">
                <Vibrate className="w-4 h-4 text-purple-400" />
              </div>
              <div>
                <div className="text-xs font-bold text-white">Haptic Feedback</div>
                <div className="text-[10px] text-gray-400">Tactile taps on button presses</div>
              </div>
            </div>

            <button
              onClick={toggleHaptic}
              className={`w-11 h-6 rounded-full transition-colors relative ${
                hapticEnabled ? 'bg-purple-600' : 'bg-gray-800'
              }`}
            >
              <div
                className={`w-4 h-4 rounded-full bg-white transition-transform absolute top-1 ${
                  hapticEnabled ? 'left-6' : 'left-1'
                }`}
              />
            </button>
          </div>
        </div>
      </GlassCard>

      {/* 5. Edge Architecture & Gateway Information */}
      <div className="rounded-2xl bg-black/25 border border-white/5 p-4 text-[11px] text-gray-400 space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="flex items-center space-x-1.5">
            <Server className="w-3.5 h-3.5 text-cyan-400" />
            <span>Core Network Protocol:</span>
          </span>
          <span className="font-mono text-white font-bold">Turso libSQL Edge v1.0</span>
        </div>
        <div className="flex items-center justify-between">
          <span>Client Encryption:</span>
          <span className="text-emerald-400 font-bold flex items-center space-x-1">
            <CheckCircle2 className="w-3 h-3 text-emerald-400" />
            <span>Telegram WebApp HMAC-SHA256</span>
          </span>
        </div>
      </div>
    </div>
  );
};
